import type { ReactNode } from 'react';
import { cn } from '../lib/utils.js';

/* ===========================================================================
 * THE BAR'S VOCABULARY — its chrome, its slot order, and the bands between.
 *
 * Split out of `ControlBar.tsx` when that file reached its 400-line ceiling:
 * `check:filesize` says an addition goes in a new file, and this is the seam
 * that was already there. `ControlBar` is about ARRANGING slots; everything
 * here is the fixed vocabulary it arranges them by — which slots exist, in
 * what order, which band each belongs to, and what a band boundary looks
 * like. None of it varies per screen, and none of it is about rendering.
 * ======================================================================== */

/** The bar itself. See the header note for why these utilities and no others. */
export const BAR_CLASS = cn(
  'flex flex-wrap items-center gap-x-control gap-y-element',
  'min-h-[3.25rem] rounded-toolbar border border-border bg-surface py-1.5 ps-1.5 pe-2',
  'shadow-[shadow:var(--shadow-resting),var(--shadow-hover)]'
);

/**
 * One slot's group. Wraps internally and is allowed to shrink (no `shrink-0`,
 * no `min-w-0` — see the header note), so a group with four filter chips in it
 * re-flows instead of overflowing.
 *
 * `gap-control` inside, matching the bar: the whole strip is one distance now,
 * and the boundaries between bands are carried by the hairline instead.
 */
export const GROUP_CLASS = 'flex flex-wrap items-center gap-control';

/**
 * The bands the dividers fall between — see the header note. The view switch
 * is in the trailing band (owner, 2026-10-31), and is its LAST control
 * (owner, 2026-11-02). `lead` is band 0: the Overview's switches, before
 * search (owner, 2026-11-02). A slot's band is a property of the slot, not of
 * what a screen put in it, so this map is the single place the grouping is
 * decided.
 */
export const BAND: Record<SlotName, 0 | 1 | 2 | 3> = {
  lead: 0,
  view: 3,
  search: 1,
  scope: 2,
  filters: 2,
  sort: 2,
  chips: 2,
  tools: 3,
  time: 2,
  segmented: 3,
  status: 3,
  freshness: 3,
  actions: 3,
  primary: 3,
};

/**
 * The fixed reading order. This array IS the guarantee (design-rules §2):
 * search LEADS, then the filter trigger, sort, the chips naming what narrows
 * and the PERIOD (owner, 2026-11-02: "date to left of action bar"); then the
 * tools; then — from the auto-margin on — the screen's state, its icon tools
 * and its one primary, and LAST the view switch (owner, 2026-11-02: "month on
 * left, toggle always on right!").
 *
 * `lead` sits before search and is the Overview's alone (§2, owner,
 * 2026-11-02: its Sites · People · Camps and Map · Table switches lead).
 * `scope` sits between search and the Filters trigger: a narrowing the owner
 * moved out of the popover by name — Dispatch's projects picker (§2, owner,
 * 2026-11-02), and nothing else.
 * `segmented` and `actions` are the slots of screens not yet migrated; they
 * keep their old places. There is no `export` slot: an export is a page
 * header action, never a bar control (owner, 2026-11-02).
 */
export const ORDER = [
  'lead',
  'search',
  'scope',
  'filters',
  'sort',
  'chips',
  'time',
  'tools',
  'segmented',
  'status',
  'freshness',
  'actions',
  'primary',
  'view',
] as const;

/** The slots that sit past the auto-margin; the first one present carries it. */
export const TRAILING: readonly SlotName[] = ['segmented', 'status', 'freshness', 'actions', 'primary', 'view'];

/**
 * THE TRAILING CLUSTER TRAVELS AS ONE (Overview, M7). Each slot used to be its
 * own item in the bar's row, so a bar that wrapped split the cluster: the view
 * switch stayed on line one against the trailing edge and the day, freshness,
 * tools and primary fell to line two at the LEADING edge — "Review 3 flagged"
 * stranded under the search box. Grouped, the cluster wraps whole and stays
 * pinned to the trailing edge (its own `sm:ms-auto`; the first slot's keeps
 * each internal line trailing too).
 *
 * `contents` on a phone: the wrapper is no box there, so the phone's row is
 * exactly what it was — the slots are the bar's own items, a view switch takes
 * its full line and nothing is auto-margined.
 */
export const TRAILING_CLASS = 'contents sm:flex sm:flex-wrap sm:items-center sm:gap-x-control sm:gap-y-element sm:ms-auto';

export type SlotName = (typeof ORDER)[number];

/**
 * The hairline between two bands.
 *
 * It is rendered as the FIRST CHILD of the group that opens a band rather than
 * as a sibling between the two groups, and that is load-bearing: the trailing
 * cluster carries `ms-auto`, so a divider sitting outside it would be left
 * behind at the leading edge with the auto-margin's gap opening up after it. As
 * the group's own first child it travels with the group to the trailing edge.
 *
 * `aria-hidden` and no text: it is a visual boundary, and the grouping it draws
 * is already carried for assistive tech by each control's own label.
 */
export function Divider(): JSX.Element {
  return (
    <span
      aria-hidden="true"
      data-control-bar-divider=""
      /* NOT ON A PHONE. The bands it separates only exist in a bar wide
         enough to hold them in a row; once the bar wraps, a hairline is as
         likely to land at the START of a line, dividing nothing. The grouping
         it draws is already carried for assistive tech by each control's own
         label. */
      className="hidden h-6 w-px flex-none self-center bg-border sm:block"
    />
  );
}

/**
 * Is there anything in this slot worth a group box?
 *
 * `ReactNode` includes the values React renders as NOTHING — `undefined`,
 * `null`, `false` and `''` — and call sites reach for every one of them
 * (`{canExport && <Button/>}` yields `false`, not `undefined`). A bare
 * `{slot && …}` truthiness test would leave an empty group in the flex row,
 * and an empty group still consumes a gap: the bar would grow a hole exactly
 * where a conditional control isn't — and, now, a hairline with nothing on
 * either side of it.
 */
export function filled(slot: ReactNode): boolean {
  return slot != null && slot !== false && slot !== '';
}
